import { useState } from 'react';
import { sendEbookEmail } from '../../api-client';
import { useNavigate } from 'react-router-dom';
import { EbookFormData } from '../../../../backend/src/shared/types';
import { FaUser, FaEnvelope, FaPhone } from 'react-icons/fa';

interface Ebook {
    id: number;
    title: string;
    url: string;
    thumbnail: string;
}

interface PopupProps {
    onClose: () => void;
    ebook: Ebook;
}

const Popup: React.FC<PopupProps> = ({ onClose, ebook }) => {
    const [formData, setFormData] = useState<EbookFormData>({ name: '', email: '', phone: '', ebookName: ebook.title });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const navigate = useNavigate();

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
    
        if (!formData.name.trim()) newErrors.name = 'Full Name is required';
        if (!formData.email.trim()) newErrors.email = 'E-Mail is required';
        if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) newErrors.email = 'Invalid E-Mail format';
        if (!formData.phone.trim()) newErrors.phone = 'Phone Number is required';
        if (!/^\d{10,15}$/.test(formData.phone)) newErrors.phone = 'Invalid Phone Number format';
    
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (validateForm()) {
            setLoading(true);
            try {
                await sendEbookEmail(formData);
                setLoading(false);
                alert('Message sent successfully!');

                // Trigger the download of the PDF
                const link = document.createElement('a');
                link.href = ebook.url;
                link.download = ebook.title;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                navigate('/thank-you');
                onClose();
            } catch (error) {
                setLoading(false);
                alert('Failed to send message');
                console.error('Error sending message:', error);
            }
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-10 mt-16">
            <div className="bg-white p-6 rounded-lg shadow-lg relative max-w-2xl w-full flex flex-col md:flex-row">
                <button
                    onClick={onClose}
                    className="absolute top-2 right-3 text-gray-600 hover:text-gray-800 transition-colors text-2xl"
                    aria-label="Close"
                >
                    &times;
                </button>
                
                {/* Thumbnail on the left for desktop */}
                <div className="mb-6 md:mb-0 md:mr-6 md:w-1/3 flex justify-center items-center">
                    <img src={ebook.thumbnail} alt={ebook.title} className="w-48 md:h-full h-48 md:object-cover object-contain rounded-lg" />
                </div>

                {/* Form on the right */}
                <div className="md:w-2/3">
                    <div className="text-center mb-6 md:mb-8">
                        <h2 className="text-2xl font-semibold text-gray-800">{ebook.title}</h2>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <div className="flex items-center border rounded w-full py-2 px-3 mt-1">
                                <FaUser className="text-sm text-halo-pink mr-4" />
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Full Name"
                                    required
                                    className="border-none w-full text-gray-700 leading-tight focus:outline-none"
                                />
                            </div>
                            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                        </div>
                        <div className="mb-4">
                            <div className="flex items-center border rounded w-full py-2 px-3 mt-1">
                                <FaEnvelope className="text-sm text-halo-pink mr-4" />
                                <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Email"
                                    required
                                    className="border-none w-full text-gray-700 leading-tight focus:outline-none"
                                />
                            </div>
                            {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                        </div>
                        <div className="mb-6">
                            <div className="flex items-center border rounded w-full py-2 px-3 mt-1">
                                <FaPhone className="text-sm rotate-90 text-halo-pink mr-4" />
                                <input
                                    type="text"
                                    name="phone"
                                    id="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="Phone Number"
                                    required
                                    className="border-none w-full text-gray-700 leading-tight focus:outline-none"
                                />
                            </div>
                            {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
                        </div>
                        <input type='hidden' value={formData.ebookName} onChange={handleChange}/>
                        <div className="flex justify-center md:mt-8">  {/* Center the button on mobile, left-align on desktop */}
                            <button
                                type="submit"
                                className="w-full md:w-1/2 bg-halo-pink text-white py-2 rounded-lg font-semibold hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500"
                                disabled={loading}
                            >
                                {loading ? 'Sending...' : 'Submit'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Popup;
